import React, { useState, useMemo, useEffect } from 'react';
import useEscapeKey from '../../../hooks/useEscapeKey';
import Modal from '../../../components/Modal';
import { API_BASE, COUNTRIES } from '../utils/constants';
import { isValidEmail } from '../utils/helpers';
import { formatCalendarDate, formatDismissalText, formatInstant, officeForCountry, officeToday } from '../utils/dismissalReport';

export default function EmployeesTab({ employees, setEmployees, policies, user, loading }) {
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [editData, setEditData] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('email');
  const [sortOrder, setSortOrder] = useState('asc');
  const [dismissalModalOpen, setDismissalModalOpen] = useState(false);
  const [dismissalReport, setDismissalReport] = useState(null);
  const [dismissalLoading, setDismissalLoading] = useState(false);
  const [dismissalDate, setDismissalDate] = useState('');
  const [selectedEmployeeEmail, setSelectedEmployeeEmail] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [scheduledByEmail, setScheduledByEmail] = useState({});

  const [newEmp, setNewEmp] = useState({
    first_name: "",
    last_name: "",
    email: "",
    manager_email: "",
    start_date: "",
    title: "",
    country: "US",
    policies: [],
    referred_by: "",
  });

  // Fuzzy search function
  const fuzzyMatch = (searchTerm, text) => {
    if (!searchTerm || !text) return true;
    
    const search = searchTerm.toLowerCase();
    const target = text.toLowerCase();
    
    // Direct substring match (most relevant)
    if (target.includes(search)) return true;
    
    // Fuzzy character sequence match
    let searchIndex = 0;
    for (let i = 0; i < target.length && searchIndex < search.length; i++) {
      if (target[i] === search[searchIndex]) {
        searchIndex++;
      }
    }
    return searchIndex === search.length;
  };

  // Handle escape key to close modals
  useEscapeKey(() => {
    if (showModal) {
      setShowModal(false);
    }
    if (editModalOpen) {
      handleEditCancel();
    }
  });

  // Filter employees based on search term
  const filteredEmployees = useMemo(() => {
    if (!searchTerm.trim()) return employees;
    
    const filtered = {};
    Object.entries(employees).forEach(([email, emp]) => {
      const searchableFields = [
        email,
        emp.first_name || '',
        emp.last_name || '',
        `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
        emp.title || '',
        emp.manager_email || '',
        emp.country || ''
      ];
      
      const matches = searchableFields.some(field => 
        fuzzyMatch(searchTerm, field)
      );
      
      if (matches) {
        filtered[email] = emp;
      }
    });
    
    return filtered;
  }, [employees, searchTerm]);

  // Memoized, dynamically sorted list of employee emails
  const employeeEmails = useMemo(() => {
    const emails = Object.keys(filteredEmployees);
    emails.sort((a, b) => {
      let valA, valB;
      
      if (sortField === 'email') {
        valA = a.toLowerCase();
        valB = b.toLowerCase();
      } else if (sortField === 'start_date') {
        // Special handling for date sorting
        const dateA = filteredEmployees[a]?.start_date;
        const dateB = filteredEmployees[b]?.start_date;
        
        // Handle missing dates (put them at the end)
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        // Convert to Date objects for proper comparison
        valA = new Date(dateA);
        valB = new Date(dateB);
        
        // Handle invalid dates
        if (isNaN(valA) && isNaN(valB)) return 0;
        if (isNaN(valA)) return 1;
        if (isNaN(valB)) return -1;
      } else {
        // Standard string sorting for other fields
        valA = (filteredEmployees[a]?.[sortField] || '').toLowerCase();
        valB = (filteredEmployees[b]?.[sortField] || '').toLowerCase();
      }
      
      // Perform comparison
      if (sortField === 'start_date') {
        // Date comparison
        if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      } else {
        // String comparison
        if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }
    });
    return emails;
  }, [filteredEmployees, sortField, sortOrder]);

  // Handle header click to toggle sorting
  const handleSort = (field) => {
    if (field === sortField) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleNewChange = (field, value) => {
    setNewEmp((prev) => ({ ...prev, [field]: value }));
  };

  const handleNewPolicySelect = (e) => {
    const selected = Array.from(e.target.selectedOptions).map(o => o.value);
    setNewEmp(prev => ({ ...prev, policies: selected }));
  };

  // Calculate referral bonus date (9 months after start date)
  const calculateReferralBonusDate = (startDate) => {
    if (!startDate || !newEmp.referred_by) return '';
    
    try {
      const start = new Date(startDate);
      const bonusDate = new Date(start);
      bonusDate.setMonth(bonusDate.getMonth() + 9);
      return bonusDate.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const handleAddEmployee = async () => {
    if (!newEmp.email) {
      alert("Email is required");
      return;
    }
    if (!isValidEmail(newEmp.email)) {
      alert("Please enter a valid email address");
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/employees`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-Email": user?.email || "",
        },
        credentials: "include",
        body: JSON.stringify({
          email: newEmp.email,
          first_name: newEmp.first_name,
          last_name: newEmp.last_name,
          manager_email: newEmp.manager_email,
          start_date: newEmp.start_date,
          title: newEmp.title,
          country: newEmp.country,
          policies: newEmp.policies,
          referred_by: newEmp.referred_by,
          referral_bonus_date: calculateReferralBonusDate(newEmp.start_date)
        }),
      });
      if (!res.ok) throw new Error(`Add failed: ${res.status}`);
      const data = await res.json();
      setEmployees((prev) => ({ ...prev, [newEmp.email.toLowerCase()]: data.employee }));
      
      // Reset form
      setNewEmp({
        first_name: "",
        last_name: "",
        email: "",
        manager_email: "",
        start_date: "",
        title: "",
        country: "US",
        policies: [],
        referred_by: "",
      });
      setShowModal(false);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const beginEdit = (email) => {
    setEditingEmail(email);
    setEditData({ ...employees[email], email });
    setEditModalOpen(true);
  };

  const handleEditChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditPolicySelect = (e) => {
    const selected = Array.from(e.target.selectedOptions).map(o => o.value);
    setEditData(prev => ({ ...prev, policies: selected }));
  };

  const handleEditSave = async () => {
    if (!editData) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/employees`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-Email": user?.email || "",
        },
        credentials: "include",
        body: JSON.stringify(editData),
      });
      if (!res.ok) throw new Error(`Save failed: ${res.status}`);
      const data = await res.json();
      setEmployees((prev) => ({ ...prev, [editData.email.toLowerCase()]: data.employee }));
      handleEditCancel();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditingEmail(null);
    setEditData(null);
    setEditModalOpen(false);
  };

  const handleDeleteEmployee = async () => {
    if (!editingEmail) return;
    const confirm = window.prompt('Type DELETE to permanently remove this employee.');
    if (confirm !== 'DELETE') return;
    
    try {
      setSaving(true);
      const res = await fetch(`${API_BASE}/api/admin/employees/${encodeURIComponent(editingEmail)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user.email,
        },
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to delete');
      
      setEmployees(prev => {
        const copy = { ...prev };
        delete copy[editingEmail];
        return copy;
      });
      handleEditCancel();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const fetchDismissalReport = async (email, asOfDate = null) => {
    setDismissalLoading(true);
    setDismissalReport(null);
    try {
      let url = `${API_BASE}/api/admin/dismissal_report/${encodeURIComponent(email)}`;
      if (asOfDate) {
        url += `?as_of_date=${asOfDate}`;
      }
      const res = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || '',
        },
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to fetch report');
      setDismissalReport(data.report);
      setDismissalModalOpen(true);
    } catch (e) {
      alert(`Error fetching dismissal report: ${e.message}`);
    } finally {
      setDismissalLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.email) return;
    let cancelled = false;
    const loadScheduled = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/dismissals`, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': user.email,
          },
          credentials: 'include',
        });
        const data = await res.json();
        if (!res.ok || !data.success || cancelled) return;
        const byEmail = {};
        (data.dismissals || []).forEach((row) => {
          if (row.status === 'scheduled') byEmail[row.employee_email] = row;
        });
        setScheduledByEmail(byEmail);
      } catch (e) {
        console.error(e);
      }
    };
    loadScheduled();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const scheduleDismissal = async () => {
    const office = officeForCountry(employees[selectedEmployeeEmail]?.country);
    const confirmed = window.confirm(
      `Schedule dismissal for ${selectedEmployeeEmail}?\n\nLast day: ${formatCalendarDate(dismissalDate)} on the ${office.label} calendar.\n\nThey stay active through the end of that day and move to Dismissed at midnight at the start of the next day in ${office.label}.`
    );
    if (!confirmed) return;
    setScheduling(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/dismissals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || '',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: selectedEmployeeEmail,
          last_day: dismissalDate,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to schedule dismissal');
      if (data.dismissal.status === 'dismissed') {
        setEmployees((prev) => {
          const copy = { ...prev };
          delete copy[selectedEmployeeEmail];
          return copy;
        });
        alert('That last day is already over in their office, so they are on the Dismissed tab now.');
      } else {
        setScheduledByEmail((prev) => ({ ...prev, [selectedEmployeeEmail]: data.dismissal }));
        alert(`Dismissal scheduled. They move to Dismissed ${formatInstant(data.dismissal.effective_at, data.dismissal.timezone)}.`);
      }
      setDismissalModalOpen(false);
      setDismissalReport(null);
      setSelectedEmployeeEmail('');
      setDismissalDate('');
    } catch (e) {
      alert(e.message);
    } finally {
      setScheduling(false);
    }
  };

  const handleDismissalClick = (email) => {
    setSelectedEmployeeEmail(email);
    // Today in their office, not UTC. UTC is already the next day during
    // the evening in San Francisco.
    const office = officeForCountry(employees[email]?.country);
    setDismissalDate(officeToday(office.timeZone));
    // Show modal to select date
    setDismissalModalOpen(true);
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 className="content-title">Employee Directory</h2>
        <button className="primary-button" onClick={() => setShowModal(true)}>+ Add Employee</button>
      </div>

      {loading && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          padding: '60px', 
          fontSize: '16px', 
          color: 'var(--symbolica-muted)' 
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px' 
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              border: '2px solid #e5e7eb',
              borderTop: '2px solid var(--symbolica-black)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            Loading employees...
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {!loading && (
        <>
          {/* Search Bar */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)' }}>
                Search:
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, title, manager..."
                style={{
                  flex: 1,
                  maxWidth: '400px',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    background: '#F0EDE6',
                    color: 'var(--symbolica-muted)',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    padding: '6px 8px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Clear
                </button>
              )}
            </div>
            {searchTerm && (
              <div style={{ marginTop: 8, fontSize: '14px', color: 'var(--symbolica-muted)' }}>
                Showing {employeeEmails.length} of {Object.keys(employees).length} employees
              </div>
            )}
          </div>

          {/* Employee Table */}
          <div className="table-scroll">
            <table className="emp-table">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>
                    Email {sortField === 'email' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('first_name')}>
                    First Name {sortField === 'first_name' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('last_name')}>
                    Last Name {sortField === 'last_name' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('title')}>
                    Title {sortField === 'title' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('manager_email')}>
                    Manager {sortField === 'manager_email' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('start_date')}>
                    Start Date {sortField === 'start_date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('country')}>
                    Country {sortField === 'country' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('referred_by')}>
                    Referred By {sortField === 'referred_by' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                  </th>
                  <th>Policies</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {employeeEmails.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '2rem', color: 'var(--symbolica-muted)' }}>
                      {searchTerm ? 'No employees match your search.' : 'No employees found.'}
                    </td>
                  </tr>
                ) : (
                  employeeEmails.map((email) => {
                    const emp = filteredEmployees[email];
                    return (
                      <tr key={email}>
                        <td>
                          {email}
                          {scheduledByEmail[email] && (
                            <div style={{ fontSize: 11, color: '#b45309', marginTop: 2 }}>
                              Leaves {formatCalendarDate(scheduledByEmail[email].last_day)}
                            </div>
                          )}
                        </td>
                        <td>{emp.first_name}</td>
                        <td>{emp.last_name}</td>
                        <td>{emp.title}</td>
                        <td>{emp.manager_email}</td>
                        <td>{emp.start_date}</td>
                        <td>{emp.country}</td>
                        <td>
                          {emp.referred_by ? (
                            <span style={{ 
                              fontSize: 12, 
                              color: '#059669',
                              fontWeight: '500'
                            }}>
                              {emp.referred_by}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--symbolica-muted)', fontSize: 12 }}>None</span>
                          )}
                        </td>
                        <td>
                          {emp.policies?.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {emp.policies.map(policyId => {
                                const policy = policies.find(p => (p.id || p.name) === policyId);
                                return (
                                  <span 
                                    key={policyId} 
                                    style={{
                                      fontSize: 11,
                                      padding: '2px 6px',
                                      borderRadius: 3,
                                      background: policy?.policy_type === 'sick' ? '#fef3c7' : '#dbeafe',
                                      color: policy?.policy_type === 'sick' ? '#92400e' : '#1e40af'
                                    }}
                                  >
                                    {policy ? policy.name : policyId}
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            <span style={{ color: 'var(--symbolica-muted)', fontSize: 12 }}>None</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button className="secondary-button" style={{ fontSize: 12, padding: '4px 8px' }} onClick={() => beginEdit(email)}>
                              Edit
                            </button>
                            <button 
                              className="secondary-button" 
                              style={{ 
                                fontSize: 12, 
                                padding: '4px 8px',
                                background: '#fee2e2',
                                color: '#dc2626',
                                border: '1px solid #fca5a5'
                              }} 
                              onClick={() => handleDismissalClick(email)}
                              disabled={dismissalLoading}
                            >
                              Dismissal Report
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Add Employee Modal */}
      {showModal && (
        <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <h3 style={{ marginBottom: 16 }}>Add Employee</h3>
          <div className="modal-form">
            <label className="input-label">First Name
              <input value={newEmp.first_name} onChange={(e) => handleNewChange('first_name', e.target.value)} />
            </label>
            
            <label className="input-label">Last Name
              <input value={newEmp.last_name} onChange={(e) => handleNewChange('last_name', e.target.value)} />
            </label>
            
            <label className="input-label">Google Email
              <input type="email" value={newEmp.email} onChange={(e) => handleNewChange('email', e.target.value)} placeholder="user@symbolica.ai" />
            </label>
            
            <label className="input-label">Job Title
              <input value={newEmp.title} onChange={(e) => handleNewChange('title', e.target.value)} placeholder="Software Engineer, Product Manager, etc." />
            </label>
            
            <label className="input-label">Country
              <select value={newEmp.country} onChange={(e) => handleNewChange('country', e.target.value)}>
                {COUNTRIES.map(country => (
                  <option key={country.value} value={country.value}>{country.label}</option>
                ))}
              </select>
            </label>
            
            <label className="input-label">Manager Email
              <input list="manager-options" value={newEmp.manager_email} onChange={(e) => handleNewChange('manager_email', e.target.value)} placeholder="Select or type manager's email" />
              <datalist id="manager-options">
                {Object.keys(employees).map(em => <option key={em} value={em} />)}
              </datalist>
            </label>
            
            <label className="input-label">Start Date
              <input type="date" value={newEmp.start_date} onChange={(e) => handleNewChange('start_date', e.target.value)} />
            </label>
            
            <label className="input-label">Referred By (Optional)
              <input 
                list="referrer-options" 
                value={newEmp.referred_by} 
                onChange={(e) => handleNewChange('referred_by', e.target.value)} 
                placeholder="Select or type referrer's email" 
              />
              <datalist id="referrer-options">
                {Object.keys(employees).map(em => <option key={em} value={em} />)}
              </datalist>
            </label>
            
            <label className="input-label">PTO Policies (Ctrl/Cmd+Click for multiple)
              <select 
                multiple 
                size={Math.min(10, policies.length || 10)} 
                value={newEmp.policies} 
                onChange={handleNewPolicySelect}
                style={{ minHeight: 160, maxHeight: 300, overflowY: 'auto' }}
              >
                {policies.map(p => (<option key={p.id || p.name} value={p.id || p.name}>{p.name}</option>))}
              </select>
            </label>
          </div>
          <div style={{ marginTop: 20, textAlign: 'right' }}>
            <button className="secondary-button" onClick={() => setShowModal(false)}>Cancel</button>
            <button className="primary-button" onClick={handleAddEmployee} disabled={saving}>
              {saving ? 'Saving…' : 'Add'}
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Employee Modal */}
      {editModalOpen && editData && (
        <Modal isOpen={editModalOpen} onClose={handleEditCancel}>
          <h3 style={{ marginBottom: 16 }}>Edit Employee</h3>
          <div className="modal-form">
            <label className="input-label">First Name
              <input 
                value={editData.first_name || ''} 
                onChange={(e) => handleEditChange('first_name', e.target.value)} 
              />
            </label>
            
            <label className="input-label">Last Name
              <input 
                value={editData.last_name || ''} 
                onChange={(e) => handleEditChange('last_name', e.target.value)} 
              />
            </label>
            
            <label className="input-label">Google Email
              <input 
                value={editData.email} 
                disabled 
                style={{ background: '#F6F4F0', color: 'var(--symbolica-muted)' }}
              />
            </label>
            
            <label className="input-label">Job Title
              <input 
                value={editData.title || ''} 
                onChange={(e) => handleEditChange('title', e.target.value)} 
                placeholder="Software Engineer, Product Manager, etc."
              />
            </label>
            
            <label className="input-label">Country
              <select 
                value={editData.country || 'US'} 
                onChange={(e) => handleEditChange('country', e.target.value)}
              >
                {COUNTRIES.map(country => (
                  <option key={country.value} value={country.value}>{country.label}</option>
                ))}
              </select>
            </label>
            
            <label className="input-label">Manager Email
              <input 
                list="manager-options-edit" 
                value={editData.manager_email || ''} 
                onChange={(e) => handleEditChange('manager_email', e.target.value)} 
                placeholder="Select or type manager's email"
              />
              <datalist id="manager-options-edit">
                {Object.keys(employees).map(em => <option key={em} value={em} />)}
              </datalist>
            </label>
            
            <label className="input-label">Start Date
              <input 
                type="date" 
                value={editData.start_date || ''} 
                onChange={(e) => handleEditChange('start_date', e.target.value)} 
              />
            </label>
            
            <label className="input-label">Referred By (Optional)
              <input 
                list="referrer-options-edit" 
                value={editData.referred_by || ''} 
                onChange={(e) => handleEditChange('referred_by', e.target.value)} 
                placeholder="Select or type referrer's email" 
              />
              <datalist id="referrer-options-edit">
                {Object.keys(employees).map(em => <option key={em} value={em} />)}
              </datalist>
            </label>
            
            <label className="input-label">PTO Policies (Ctrl/Cmd+Click for multiple)
              <select 
                multiple 
                size={Math.min(10, policies.length || 10)}
                value={editData.policies || []} 
                onChange={handleEditPolicySelect}
                style={{ minHeight: 160, maxHeight: 300, overflowY: 'auto' }}
              >
                {policies.map(p => (
                  <option key={p.id || p.name} value={p.id || p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button 
              style={{ 
                background: '#e11d48', 
                color: '#fff', 
                border: 'none', 
                padding: '8px 16px', 
                borderRadius: 6, 
                cursor: 'pointer' 
              }} 
              onClick={handleDeleteEmployee} 
              disabled={saving}
            >
              Delete
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="secondary-button" onClick={handleEditCancel}>Cancel</button>
              <button className="primary-button" onClick={handleEditSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Dismissal Report Modal */}
      {dismissalModalOpen && (
        <Modal isOpen={dismissalModalOpen} onClose={() => {
          setDismissalModalOpen(false);
          setDismissalReport(null);
          setSelectedEmployeeEmail('');
          setDismissalDate('');
        }}>
          <div style={{ 
            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
            margin: '-16px -16px 16px -16px',
            padding: '20px',
            borderRadius: '8px 8px 0 0',
            color: 'white'
          }}>
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
              Dismissal Balance Report
            </h3>
            <div style={{ fontSize: '14px', opacity: 0.9, marginTop: '4px' }}>
              {dismissalReport ? 
                `As of ${formatCalendarDate(dismissalReport.dismissal_date)}` :
                `Select dismissal date for ${selectedEmployeeEmail}`
              }
            </div>
          </div>
          
          {!dismissalReport ? (
            // Date selection UI
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: 'var(--symbolica-black)',
                  marginBottom: '8px' 
                }}>
                  Select Dismissal Date
                </label>
                <input
                  type="date"
                  value={dismissalDate}
                  onChange={(e) => setDismissalDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <div style={{ 
                  marginTop: '8px', 
                  fontSize: '13px', 
                  color: 'var(--symbolica-muted)' 
                }}>
                  This is their last calendar day in their office. London, San Francisco, and Australia
                  are each calculated on local time, so a Friday last day stays active through Friday evening there.
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setDismissalModalOpen(false);
                    setSelectedEmployeeEmail('');
                    setDismissalDate('');
                  }}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    background: 'white',
                    color: 'var(--symbolica-black)',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => fetchDismissalReport(selectedEmployeeEmail, dismissalDate)}
                  disabled={!dismissalDate || dismissalLoading}
                  style={{
                    padding: '10px 20px',
                    background: dismissalLoading ? 'var(--symbolica-muted)' : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    fontWeight: '500',
                    cursor: dismissalLoading || !dismissalDate ? 'not-allowed' : 'pointer'
                  }}
                >
                  {dismissalLoading ? 'Generating...' : 'Generate Report'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <pre style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '13px',
                lineHeight: '1.7',
                whiteSpace: 'pre-wrap',
                background: '#F6F4F0',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '16px',
                margin: '0 0 16px 0',
                color: 'var(--symbolica-black)',
                userSelect: 'all',
              }}>
                {formatDismissalText(dismissalReport)}
              </pre>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                <button
                  onClick={() => {
                    setDismissalReport(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    background: 'white',
                    color: 'var(--symbolica-black)',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Change Date
                </button>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => navigator.clipboard.writeText(formatDismissalText(dismissalReport))}
                    style={{
                      padding: '10px 20px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: 'white',
                      color: 'var(--symbolica-black)',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Copy
                  </button>
                <button
                  onClick={scheduleDismissal}
                  disabled={scheduling || !!scheduledByEmail[selectedEmployeeEmail]}
                  style={{
                    padding: '10px 20px',
                    background: scheduling || scheduledByEmail[selectedEmployeeEmail] ? 'var(--symbolica-muted)' : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    fontWeight: '500',
                    cursor: scheduling || scheduledByEmail[selectedEmployeeEmail] ? 'not-allowed' : 'pointer'
                  }}
                >
                  {scheduledByEmail[selectedEmployeeEmail]
                    ? 'Already scheduled'
                    : scheduling
                      ? 'Scheduling...'
                      : 'Dismiss on this date'}
                </button>
                <button
                  className="primary-button"
                  onClick={() => {
                    setDismissalModalOpen(false);
                    setDismissalReport(null);
                    setSelectedEmployeeEmail('');
                    setDismissalDate('');
                  }}
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
                </div>
              </div>
            </>
          )}
        </Modal>
      )}
    </>
  );
} 